import { create } from 'zustand';

import { wallStorage, wallsApi } from '@walls/services/wallsApi';
import { useWallContentStore } from './wallContentStore';
import { clearLockedElementSelection } from './lockedSelection';
import type { WallBoard, WallId, WallTheme } from './types';
import { emptyWallTheme, normalizeWallTheme } from './wallTheme';

export type BoardSetting =
  | 'allowDrawingTools'
  | 'allowCardCreation'
  | 'autoAlignCards'
  | 'allowClustering'
  | 'allowCardResizing'
  | 'allowCardLocking'
  | 'allowCardUnlocking'
  | 'allowAiTools'
  | 'showCardOwnerNames';

interface WallStoreState {
  boardId: WallId | null;
  board: WallBoard | null;
  topicKey: string | null;
  authenticationBoard: Pick<WallBoard, 'id' | 'title' | 'isPublic'> | null;
  requiresAuthentication: boolean;
  isCheckingAuthentication: boolean;
  isAuthenticatingWall: boolean;
  authenticationError: string | null;
  theme: WallTheme;
  isLoadingTheme: boolean;
  themeLoadError: string | null;
  isLoadingWall: boolean;
  wallLoadError: string | null;
}

interface WallStoreActions {
  setTopicKey: (topicKey: string | null) => void;
  checkWallAuthentication: (boardId: WallId) => Promise<boolean>;
  authenticateWall: (boardId: WallId, password: string) => Promise<void>;
  loadWall: (boardId: WallId) => Promise<void>;
  loadWallTheme: (boardId: WallId) => Promise<void>;
  setTheme: (theme: Partial<WallTheme>) => void;
  patchBoard: (patch: Partial<WallBoard>) => void;
  updateBoardSetting: (setting: BoardSetting, enabled: boolean) => Promise<void>;
  updateElementLocking: (enabled: boolean) => Promise<void>;
  resetWall: () => void;
}

export type WallStore = WallStoreState & WallStoreActions;

export const useWallStore = create<WallStore>((set, get) => ({
  boardId: null,
  board: null,
  topicKey: null,
  authenticationBoard: null,
  requiresAuthentication: false,
  isCheckingAuthentication: false,
  isAuthenticatingWall: false,
  authenticationError: null,
  theme: emptyWallTheme,
  isLoadingTheme: false,
  themeLoadError: null,
  isLoadingWall: false,
  wallLoadError: null,

  setTopicKey: (topicKey) => set({ topicKey }),

  setTheme: (theme) => set({ theme: normalizeWallTheme(theme), themeLoadError: null }),

  patchBoard: (patch) =>
    set((state) => ({ board: state.board ? { ...state.board, ...patch } : null })),

  checkWallAuthentication: async (boardId) => {
    const storedTopicKey = wallStorage.getTopicKey(boardId);

    set({
      boardId,
      board: null,
      topicKey: storedTopicKey,
      authenticationBoard: null,
      requiresAuthentication: false,
      isCheckingAuthentication: true,
      authenticationError: null,
      wallLoadError: null,
    });
    useWallContentStore.getState().resetBoardContent();

    if (storedTopicKey) {
      set({ isCheckingAuthentication: false });
      return false;
    }

    try {
      const status = await wallsApi.getBoardRequiresAuthentication('topic', boardId);

      if (status.requiresAuthentication) {
        set({
          authenticationBoard: status.board ?? null,
          requiresAuthentication: true,
          isCheckingAuthentication: false,
        });
        return true;
      }

      set({
        authenticationBoard: null,
        requiresAuthentication: false,
        isCheckingAuthentication: false,
      });
      return false;
    } catch (error) {
      set({
        authenticationBoard: null,
        requiresAuthentication: false,
        isCheckingAuthentication: false,
        wallLoadError: error instanceof Error ? error.message : 'Error retrieving wall',
      });
      return true;
    }
  },

  authenticateWall: async (boardId, password) => {
    set({ isAuthenticatingWall: true, authenticationError: null });

    try {
      const response = await wallsApi.authenticateBoard(boardId, password, get().topicKey);
      const topicKey = response.key?.trim() ?? null;

      if (topicKey) {
        wallStorage.setTopicKey(boardId, topicKey);
      }

      set({
        topicKey,
        authenticationBoard: null,
        requiresAuthentication: false,
        authenticationError: null,
      });

      await get().loadWall(boardId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to authenticate wall';

      set({
        authenticationError: message.toLowerCase().includes('incorrect password')
          ? 'Incorrect password, please try again'
          : message,
      });
    } finally {
      set({ isAuthenticatingWall: false });
    }
  },

  loadWall: async (boardId) => {
    set({
      boardId,
      board: null,
      authenticationBoard: null,
      requiresAuthentication: false,
      authenticationError: null,
      theme: emptyWallTheme,
      isLoadingTheme: false,
      themeLoadError: null,
      isLoadingWall: true,
      wallLoadError: null,
    });
    useWallContentStore.getState().resetBoardContent();

    try {
      const board = await wallsApi.getBoard(boardId, get().topicKey);
      set({ board, boardId: board.id ?? boardId });

      await Promise.all([
        get().loadWallTheme(board.id ?? boardId),
        useWallContentStore
          .getState()
          .loadBoardContent(board.id ?? boardId, { topicKey: get().topicKey }),
      ]);
    } catch (error) {
      set({
        board: null,
        wallLoadError: error instanceof Error ? error.message : 'Error retrieving wall',
      });
      useWallContentStore.getState().resetBoardContent();
    } finally {
      set({ isLoadingWall: false });
    }
  },

  loadWallTheme: async (boardId) => {
    set({ isLoadingTheme: true, themeLoadError: null });

    try {
      const theme = await wallsApi.getWallTheme(boardId, get().topicKey);
      set({ theme: normalizeWallTheme(theme) });
    } catch (error) {
      set({
        theme: emptyWallTheme,
        themeLoadError: error instanceof Error ? error.message : 'Error retrieving wall theme',
      });
    } finally {
      set({ isLoadingTheme: false });
    }
  },

  updateBoardSetting: async (setting, enabled) => {
    const { board, topicKey } = get();

    if (!board || !board.isOwner) {
      return;
    }

    const previousBoard = board;
    const nextBoard = { ...board, [setting]: enabled };
    set({ board: nextBoard, wallLoadError: null });
    clearLockedSelectionsWhenUnlockingDisabled(setting, enabled);

    try {
      await updateBoardSetting(board.id, setting, enabled, topicKey);
      void import('@walls/services/boardWebsockets').then(({ publishBoardSocketPackage }) => {
        publishBoardSocketPackage({
          action: getBoardSettingSocketAction(setting, enabled),
          topic: board.id,
        });
      });
    } catch (error) {
      set({
        board: previousBoard,
        wallLoadError: error instanceof Error ? error.message : 'Error updating wall setting',
      });
    }
  },

  updateElementLocking: async (enabled) => {
    const { board, topicKey } = get();

    if (!board || !board.isOwner) {
      return;
    }

    const previousBoard = board;
    const nextBoard = {
      ...board,
      allowCardLocking: enabled,
      allowCardUnlocking: enabled,
    };
    set({ board: nextBoard, wallLoadError: null });
    if (!enabled) {
      clearLockedElementSelection();
    }

    try {
      await Promise.all([
        wallsApi.setAllowCardLocking(board.id, enabled, topicKey),
        wallsApi.setAllowCardUnlocking(board.id, enabled, topicKey),
      ]);
      void import('@walls/services/boardWebsockets').then(({ publishBoardSocketPackage }) => {
        publishBoardSocketPackage({
          action: getBoardSettingSocketAction('allowCardLocking', enabled),
          topic: board.id,
        });
        publishBoardSocketPackage({
          action: getBoardSettingSocketAction('allowCardUnlocking', enabled),
          topic: board.id,
        });
      });
    } catch (error) {
      set({
        board: previousBoard,
        wallLoadError: error instanceof Error ? error.message : 'Error updating wall setting',
      });
    }
  },

  resetWall: () => {
    useWallContentStore.getState().resetBoardContent();

    set({
      boardId: null,
      board: null,
      topicKey: null,
      authenticationBoard: null,
      requiresAuthentication: false,
      isCheckingAuthentication: false,
      isAuthenticatingWall: false,
      authenticationError: null,
      theme: emptyWallTheme,
      isLoadingTheme: false,
      themeLoadError: null,
      isLoadingWall: false,
      wallLoadError: null,
    });
  },
}));

function updateBoardSetting(
  boardId: WallId,
  setting: BoardSetting,
  enabled: boolean,
  topicKey: string | null
) {
  switch (setting) {
    case 'allowDrawingTools':
      return wallsApi.setAllowDrawingTools(boardId, enabled, topicKey);
    case 'allowCardCreation':
      return wallsApi.setAllowCardCreation(boardId, enabled, topicKey);
    case 'autoAlignCards':
      return wallsApi.setAutoAlign(boardId, enabled, topicKey);
    case 'allowClustering':
      return wallsApi.setAllowClustering(boardId, enabled, topicKey);
    case 'allowCardResizing':
      return wallsApi.setAllowCardResizing(boardId, enabled, topicKey);
    case 'allowCardLocking':
      return wallsApi.setAllowCardLocking(boardId, enabled, topicKey);
    case 'allowCardUnlocking':
      return wallsApi.setAllowCardUnlocking(boardId, enabled, topicKey);
    case 'allowAiTools':
      return wallsApi.setAllowAiTools(boardId, enabled, topicKey);
    case 'showCardOwnerNames':
      return wallsApi.setShowCardOwnerNames(boardId, enabled, topicKey);
  }
}

function clearLockedSelectionsWhenUnlockingDisabled(setting: BoardSetting, enabled: boolean) {
  if (setting === 'allowCardUnlocking' && !enabled) {
    clearLockedElementSelection();
  }
}

function getBoardSettingSocketAction(setting: BoardSetting, enabled: boolean) {
  switch (setting) {
    case 'allowDrawingTools':
      return enabled ? 'enableDrawingTools' : 'disableDrawingTools';
    case 'allowCardCreation':
      return enabled ? 'enableCardCreation' : 'disableCardCreation';
    case 'autoAlignCards':
      return enabled ? 'enableAutoAlign' : 'disableAutoAlign';
    case 'allowClustering':
      return enabled ? 'enableClustering' : 'disableClustering';
    case 'allowCardResizing':
      return enabled ? 'enableCardResizing' : 'disableCardResizing';
    case 'allowCardLocking':
      return enabled ? 'enableCardLocking' : 'disableCardLocking';
    case 'allowCardUnlocking':
      return enabled ? 'enableCardUnlocking' : 'disableCardUnlocking';
    case 'allowAiTools':
      return enabled ? 'enableAiTools' : 'disableAiTools';
    case 'showCardOwnerNames':
      return enabled ? 'enableCardOwnerNames' : 'disableCardOwnerNames';
  }
}
